import { useMemo } from 'react';
import { Grid } from '@mui/system';
import { HEALTHCHECK_NAMES, ZONE_KEYS } from 'domain/shared/newProject';
import HealthcheckRow from '../HealthcheckRow';
import { getStaffingRiskIssueByName, getHealthcheckByName, HealthcheckMessage } from '../../shared/healthcheck';
import { HEALTHCHECK_LABELS } from '../commercial/constants';
import { getTeamCoverageStats, TEAM_COVERAGE_TOOLTIP } from '../../team/shared/teamCoverage';
import { ControlData } from '../types';

type StaffingHealthcheckProps = {
  data: ControlData;
};

const StaffingHealthchecks = ({ data }: StaffingHealthcheckProps) => {
  const healthChecks = data?.health?.healthChecks || [];
  const staffingRisksIssues = data?.staffingRisk?.issues || [];
  const teamCoverageStats = useMemo(() => {
    const plannedFte = data?.team?.id && data?.budget?.fte != null ? Number(data.budget.fte) : null;
    const assignedFte =
      data?.team?.members?.reduce(
        (sum, member) => (member.isBench || member.isBackup ? sum : sum + (Number(member.fte) || 0)),
        0,
      ) ?? null;

    return getTeamCoverageStats(assignedFte, plannedFte);
  }, [data?.budget?.fte, data?.team?.id, data?.team?.members]);

  const teamHealthcheck = getStaffingRiskIssueByName(staffingRisksIssues, HEALTHCHECK_NAMES.PROJECT_UNDERSTAFFED);
  const keyRolesHealthcheck = getStaffingRiskIssueByName(staffingRisksIssues, HEALTHCHECK_NAMES.KEY_ROLE_UNSTAFFED);
  const replacementHealthcheck = getStaffingRiskIssueByName(
    staffingRisksIssues,
    HEALTHCHECK_NAMES.REPLACEMENT_NOT_ASSIGNED,
  );
  const qualityHealthcheck = getStaffingRiskIssueByName(staffingRisksIssues, HEALTHCHECK_NAMES.TEAM_QUALITY_MISMATCH);
  const risksHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.STAFFING_RISKS);

  return (
    <Grid container direction={'column'} size={12} pl={2}>
      {teamCoverageStats?.value && (
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.TEAM}
          lastUpdateValue={teamHealthcheck?.updatedAt ?? teamHealthcheck?.createdAt}
        >
          <HealthcheckMessage
            isStaffingHealthcheck
            staffingRisksIssue={teamHealthcheck}
            messageOverride={teamCoverageStats?.value}
            zoneOverride={teamHealthcheck?.targetPriority ?? ZONE_KEYS.SAFE}
            tooltipTitle={TEAM_COVERAGE_TOOLTIP}
          />
        </HealthcheckRow>
      )}
      {keyRolesHealthcheck && (
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.ROLES}
          lastUpdateValue={keyRolesHealthcheck?.updatedAt ?? keyRolesHealthcheck?.createdAt}
        >
          <HealthcheckMessage isStaffingHealthcheck staffingRisksIssue={keyRolesHealthcheck} />
        </HealthcheckRow>
      )}
      {replacementHealthcheck && (
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.REPLACEMENT}
          lastUpdateValue={replacementHealthcheck?.updatedAt ?? replacementHealthcheck?.createdAt}
        >
          <HealthcheckMessage isStaffingHealthcheck staffingRisksIssue={replacementHealthcheck} />
        </HealthcheckRow>
      )}
      {qualityHealthcheck && (
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.QUALITY}
          lastUpdateValue={qualityHealthcheck?.updatedAt ?? qualityHealthcheck?.createdAt}
        >
          <HealthcheckMessage isStaffingHealthcheck staffingRisksIssue={qualityHealthcheck} />
        </HealthcheckRow>
      )}
      <HealthcheckRow
        label={HEALTHCHECK_LABELS.RISKS}
        lastUpdateValue={risksHealthcheck?.latestEntityModificationDate ?? risksHealthcheck?.zoneSince}
      >
        <HealthcheckMessage healthcheck={risksHealthcheck} />
      </HealthcheckRow>
    </Grid>
  );
};

export default StaffingHealthchecks;
